// Dry-run trigger + result panel for an EditablePromptCardEditor. Hidden
// when the card's `has_dry_run_fixture` flag is false (i.e. no canonical
// fixture is registered server-side for that prompt key).
//
// Slice D of the AI prompting hardening pass — see
// `docs/planning/ai_prompting_suggest_edit.md` "Hardening pass" → "Slice D".

import { useMemo, useState } from "react";
import {
  runDryRun,
  type DryRunFixtureDTO,
  type DryRunSuccessDTO,
  type DryRunFailureDTO,
} from "@/lib/aiPrompting/client";
import { buildResultRows } from "./dryRunResultFormat";

export type DryRunButtonProps = {
  promptKey: string;
  draft: string;
  disabled: boolean;
};

type DryRunState =
  | { status: "idle" }
  | { status: "running" }
  | { status: "ok"; data: DryRunSuccessDTO }
  | { status: "test_failed"; data: DryRunFailureDTO }
  | { status: "error"; message: string };

export function EditablePromptCardDryRun({ promptKey, draft, disabled }: DryRunButtonProps) {
  const [state, setState] = useState<DryRunState>({ status: "idle" });
  const [fixtureOpen, setFixtureOpen] = useState(false);

  const onRun = async () => {
    setState({ status: "running" });
    const r = await runDryRun(promptKey, draft);
    if (!r.ok) {
      setState({ status: "error", message: r.error });
      return;
    }
    const data = r.data;
    if (data.ok) {
      setState({ status: "ok", data });
    } else {
      setState({ status: "test_failed", data });
    }
  };

  const rows = useMemo(() => {
    if (state.status !== "ok") return [];
    return buildResultRows(state.data.parsed);
  }, [state]);

  return (
    <div>
      <button
        type="button"
        onClick={() => void onRun()}
        disabled={disabled || state.status === "running"}
        className="text-xs rounded-md border border-sky-300 bg-sky-50 px-2.5 py-1.5 font-semibold text-sky-900 hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-sky-700 dark:bg-sky-950/40 dark:text-sky-100"
        title={
          disabled
            ? "Resolve validation issues above before running a test."
            : "Run the draft against a canonical sample transcript and show the parsed result."
        }
      >
        {state.status === "running" ? "Testing…" : "Test draft"}
      </button>

      {state.status === "ok" ? (
        <DryRunSuccessPanel
          data={state.data}
          fixtureOpen={fixtureOpen}
          setFixtureOpen={setFixtureOpen}
          rows={rows}
        />
      ) : null}
      {state.status === "test_failed" ? <DryRunFailurePanel data={state.data} /> : null}
      {state.status === "error" ? (
        <p className="mt-3 rounded-md border border-rose-300 bg-rose-50 p-2 text-xs text-rose-900 dark:border-rose-700 dark:bg-rose-950/40 dark:text-rose-200">
          {state.message}
        </p>
      ) : null}
    </div>
  );
}

function DryRunSuccessPanel({
  data,
  fixtureOpen,
  setFixtureOpen,
  rows,
}: {
  data: DryRunSuccessDTO;
  fixtureOpen: boolean;
  setFixtureOpen: (b: boolean) => void;
  rows: Array<{ key: string; value: string; populated: boolean }>;
}) {
  const { fixture, populated_count, duration_ms } = data;
  const expected = fixture.expected_field_count;
  const healthy = populated_count >= Math.floor(expected * 0.8);
  return (
    <div className="mt-3 rounded-lg border border-sky-200 bg-sky-50/60 p-3 dark:border-sky-800 dark:bg-sky-950/30">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-baseline gap-2 text-xs">
          <span className="font-semibold text-sky-900 dark:text-sky-100">Dry-run result</span>
          <span
            className={
              healthy
                ? "rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100"
                : "rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-900 dark:bg-amber-900/40 dark:text-amber-100"
            }
          >
            {populated_count} / {expected} fields populated
          </span>
          <span className="text-sky-700 dark:text-sky-300">{duration_ms} ms</span>
        </div>
        <FixtureToggle open={fixtureOpen} setOpen={setFixtureOpen} fixture={fixture} />
      </div>
      {fixtureOpen ? <FixturePreview fixture={fixture} /> : null}
      <ul className="mt-2 grid grid-cols-1 gap-x-3 gap-y-1 text-[11px] sm:grid-cols-2">
        {rows.map((row) => (
          <li
            key={row.key}
            className={
              "flex items-baseline gap-2 " +
              (row.populated
                ? "text-gray-800 dark:text-zinc-200"
                : "text-gray-400 italic dark:text-zinc-500")
            }
          >
            <code className="font-mono">{row.key}:</code>
            <span className="min-w-0 truncate">{row.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DryRunFailurePanel({ data }: { data: DryRunFailureDTO }) {
  return (
    <div className="mt-3 rounded-lg border border-rose-300 bg-rose-50 p-3 text-xs text-rose-900 dark:border-rose-700 dark:bg-rose-950/40 dark:text-rose-200">
      <p className="font-semibold">Test failed — do not publish</p>
      <p className="mt-1">{data.error}</p>
      {data.raw ? (
        <pre className="mt-2 max-h-32 overflow-y-auto whitespace-pre-wrap rounded border border-rose-200 bg-white/60 p-2 font-mono text-[10px] text-rose-900 dark:border-rose-800 dark:bg-rose-950/60 dark:text-rose-100">
          {data.raw}
        </pre>
      ) : null}
    </div>
  );
}

function FixtureToggle({
  open,
  setOpen,
  fixture,
}: {
  open: boolean;
  setOpen: (b: boolean) => void;
  fixture: DryRunFixtureDTO;
}) {
  return (
    <button
      type="button"
      onClick={() => setOpen(!open)}
      className="text-[11px] underline text-sky-800 hover:text-sky-900 dark:text-sky-300 dark:hover:text-sky-100"
      aria-expanded={open}
      title={fixture.label}
    >
      {open ? "Hide fixture" : "Show fixture"}
    </button>
  );
}

function FixturePreview({ fixture }: { fixture: DryRunFixtureDTO }) {
  return (
    <div className="mt-2 rounded-md border border-sky-200 bg-white p-2 dark:border-sky-800 dark:bg-zinc-900/40">
      <p className="text-[11px] font-semibold text-sky-900 dark:text-sky-100">{fixture.label}</p>
      <p className="mt-1 text-[11px] text-gray-600 dark:text-zinc-300">{fixture.description}</p>
      <pre className="mt-2 max-h-32 overflow-y-auto whitespace-pre-wrap rounded border border-sky-100 bg-sky-50 p-2 font-mono text-[10px] leading-snug text-gray-800 dark:border-sky-900 dark:bg-zinc-900/60 dark:text-zinc-200">
        {fixture.transcript}
      </pre>
    </div>
  );
}
